import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { X, Loader2, CreditCard, ShieldCheck, CheckCircle, ArrowRight, ArrowLeft, Phone, MessageSquare } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { Input } from '@/components/ui/input';
import SquareApplePayButton, { loadSquareSdk, squareVerifyBuyer } from '@/components/checkout/SquareApplePayButton';
import SquareGooglePayButton from '@/components/checkout/SquareGooglePayButton';
import { usePartyAvailability } from '@/hooks/usePartyAvailability';
import PartyCalendar from './PartyCalendar';
import PartyTimeSlotGrid from './PartyTimeSlotGrid';
import DriverCountSelector from './DriverCountSelector';

const DEPOSIT_AMOUNT = 50;
const SQUARE_APP_ID = import.meta.env.VITE_SQUARE_APP_ID || 'sq0idp-HblpXluK_lgmGh2wdXzdCA';
const SQUARE_LOCATION_ID = import.meta.env.VITE_SQUARE_LOCATION_ID || 'LHHYN7QJW6P94';
const VENUE_PHONE = '0151 528 3001';

// Multi-step party deposit modal with live calendar, rig-inventory sync,
// 48-hour lead-time enforcement, driver count validation, enriched event
// detail capture (organizer name, birthday child, age confirmation), and
// flat £50 Square deposit checkout (card + Apple Pay / Google Pay).
//
// Step 1 — Date & Time: live calendar with party availability dots + time
//   slot grid showing slots where all standard rigs are free and outside
//   the 48h window.
// Step 2 — Details & Payment: organizer first/last name + email + phone,
//   birthday child name + age (8–15 dropdown), driver count selector (4–8),
//   required age-range confirmation checkbox, optional special requests,
//   and Square payment (card / Apple Pay / Google Pay).
// Step 3 — Success: deposit confirmed, staff will contact to finalise.
export default function PartyDepositModal({ open, onClose, packageTier, packageName, packagePrice, onEnquiryScroll }) {
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [visibleMonth, setVisibleMonth] = useState(new Date());
  const [driverCount, setDriverCount] = useState(4);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    contact_email: '',
    contact_phone: '',
    birthday_child_name: '',
    birthday_child_age: '',
    special_requests: '',
    age_confirmation: false,
  });
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const {
    slots, openDays, dayHint, slotAvailability, isDateWithinLeadTime, bookingsPaused,
  } = usePartyAvailability(visibleMonth);

  const dateStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '';
  const entireDateWithinLead = selectedDate && isDateWithinLeadTime(dateStr);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Reset state when the modal opens
  useEffect(() => {
    if (open) {
      setStep(1);
      setSelectedDate(null);
      setSelectedSlot('');
      setDriverCount(4);
      setError('');
      setSuccess(false);
      setForm({
        first_name: '', last_name: '', contact_email: '', contact_phone: '',
        birthday_child_name: '', birthday_child_age: '', special_requests: '', age_confirmation: false,
      });
    }
  }, [open]);

  // Init Square card form when entering step 2
  useEffect(() => {
    if (step !== 2) return;
    let destroyed = false;
    const initSquare = async () => {
      try {
        await loadSquareSdk();
        if (destroyed || !window.Square) return;
        const payments = window.Square.payments(SQUARE_APP_ID, SQUARE_LOCATION_ID);
        const cardInstance = await payments.card({
          style: {
            '.input-container': { borderColor: 'rgba(255,255,255,0.1)', borderRadius: '0px' },
            input: { color: '#fff', backgroundColor: '#111115', fontSize: '14px' },
            'input::placeholder': { color: 'rgba(255,255,255,0.2)' },
            '.message-text': { color: 'rgba(255,255,255,0.4)' },
          },
        });
        if (destroyed) { try { cardInstance.destroy(); } catch {} return; }
        await cardInstance.attach('#sq-deposit-card');
        if (!destroyed) setCard(cardInstance);
      } catch (e) {
        console.warn('[PartyDepositModal] Square init failed:', e?.message);
      }
    };
    initSquare();
    return () => {
      destroyed = true;
      setCard((c) => { if (c) { try { c.destroy(); } catch {} } return null; });
    };
  }, [step]);

  const fullName = `${form.first_name.trim()} ${form.last_name.trim()}`.trim();

  const validateContact = () => {
    if (!form.first_name.trim()) return 'Please enter your first name.';
    if (!form.last_name.trim()) return 'Please enter your last name.';
    if (!form.contact_email.trim()) return 'Please enter your email.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email)) return 'Please enter a valid email address.';
    if (!form.age_confirmation) return 'Please confirm all drivers are aged 8–15.';
    return null;
  };

  const buildVerificationDetails = () => ({
    amount: DEPOSIT_AMOUNT.toFixed(2),
    currencyCode: 'GBP',
    intent: 'CHARGE',
    billingContact: {
      familyName: form.last_name,
      givenName: form.first_name,
      email: form.contact_email,
      phone: form.contact_phone || undefined,
      countryCode: 'GB',
    },
  });

  const charge = async (sourceId, verificationToken) => {
    return await base44.functions.invoke('groupBookingDepositCheckout', {
      sourceId,
      verificationToken,
      package_tier: packageTier,
      first_name: form.first_name,
      last_name: form.last_name,
      contact_name: fullName,
      contact_email: form.contact_email,
      contact_phone: form.contact_phone,
      birthday_child_name: form.birthday_child_name,
      birthday_child_age: form.birthday_child_age,
      estimated_headcount: driverCount,
      age_confirmation: form.age_confirmation,
      party_type: 'kids',
      number_of_drivers: driverCount,
      preferred_date: dateStr,
      time_slot: selectedSlot,
      special_requests: form.special_requests,
    });
  };

  const handleCardPay = async (e) => {
    e.preventDefault();
    const vErr = validateContact();
    if (vErr) { setError(vErr); return; }
    if (!card) { setError('Card form not ready. Please wait.'); return; }
    setLoading(true);
    setError('');
    try {
      const result = await card.tokenize();
      if (result.status !== 'OK') {
        setError(result.errors?.[0]?.message || 'Card error. Please check your details.');
        setLoading(false);
        return;
      }
      let verifiedToken;
      try {
        verifiedToken = await squareVerifyBuyer(result.token, buildVerificationDetails());
      } catch (err) {
        setError(err?.message || '3D Secure verification failed. Please try again.');
        setLoading(false);
        return;
      }
      const res = await charge(result.token, verifiedToken);
      if (res.data?.success) {
        setSuccess(true);
        setStep(3);
      } else {
        setError(res.data?.error || 'Payment failed. Please try again.');
      }
    } catch (err) {
      setError(err?.message || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleWalletToken = async (token) => {
    const vErr = validateContact();
    if (vErr) { setError(vErr); return; }
    setLoading(true);
    setError('');
    try {
      let verifiedToken;
      try {
        verifiedToken = await squareVerifyBuyer(token, buildVerificationDetails());
      } catch (err) {
        setError(err?.message || 'Payment verification failed. Please try again.');
        setLoading(false);
        return;
      }
      const res = await charge(token, verifiedToken);
      if (res.data?.success) {
        setSuccess(true);
        setStep(3);
      } else {
        setError(res.data?.error || 'Payment failed. Please try again.');
      }
    } catch (err) {
      setError(err?.message || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const labelClass = 'block text-white/30 text-[10px] font-bold tracking-widest mb-1.5';
  const inputClass = 'bg-[#111115] border-white/10 text-white h-11 focus:border-primary/60';
  const estimatedTotal = driverCount > 0 ? driverCount * (packagePrice || 0) : 0;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="relative w-full max-w-3xl bg-[#0d0d0f] border border-white/10 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 z-10 text-white/40 hover:text-white transition-colors" aria-label="Close">
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="px-6 sm:px-8 pt-6 pb-4 border-b border-white/5">
          <span className="text-primary text-[11px] font-bold tracking-[0.3em]">{packageName || 'PARTY PACKAGE'}</span>
          <div className="flex items-center gap-2 mt-1">
            {['DATE & TIME', 'DETAILS & PAYMENT', 'CONFIRMED'].map((label, i) => (
              <React.Fragment key={label}>
                <span className={`text-[9px] font-bold tracking-wider ${step >= i + 1 ? 'text-primary' : 'text-white/20'}`}>
                  {i + 1}. {label}
                </span>
                {i < 2 && <span className="text-white/10">·</span>}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="p-6 sm:p-8">
          <AnimatePresence mode="wait">
            {/* ── STEP 1: DATE & TIME ── */}
            {step === 1 && (
              <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <PartyCalendar
                    selectedDate={selectedDate}
                    onSelectDate={(d) => { setSelectedDate(d); setSelectedSlot(''); }}
                    visibleMonth={visibleMonth}
                    onMonthChange={setVisibleMonth}
                    dayHints={dayHint}
                    openDays={openDays}
                  />

                  <div>
                    {selectedDate && entireDateWithinLead && !bookingsPaused ? (
                      <motion.div key="leadtime" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="border border-primary/20 bg-primary/5 p-5">
                        <p className="text-primary text-[10px] font-bold tracking-widest mb-2">LAST-MINUTE BOOKING</p>
                        <p className="text-white/60 text-sm leading-relaxed mb-4">
                          Looking to book within the next 48 hours? Track time may still be available!
                          Call us on <a href={`tel:${VENUE_PHONE.replace(/\s/g, '')}`} className="text-primary font-bold hover:underline">{VENUE_PHONE}</a> or
                          submit an enquiry to check last-minute availability.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <a href={`tel:${VENUE_PHONE.replace(/\s/g, '')}`} className="inline-flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider px-4 py-2.5 transition-colors">
                            <Phone className="w-3.5 h-3.5" /> CALL US
                          </a>
                          <button
                            type="button"
                            onClick={() => { onClose(); onEnquiryScroll?.(); }}
                            className="inline-flex items-center gap-1.5 border border-white/10 hover:border-primary/50 text-white/70 hover:text-primary font-bold text-xs tracking-wider px-4 py-2.5 transition-colors"
                          >
                            <MessageSquare className="w-3.5 h-3.5" /> SUBMIT ENQUIRY
                          </button>
                        </div>
                      </motion.div>
                    ) : selectedDate ? (
                      <PartyTimeSlotGrid
                        slots={slots}
                        slotAvailability={slotAvailability(dateStr)}
                        selectedSlot={selectedSlot}
                        onSelectSlot={setSelectedSlot}
                      />
                    ) : (
                      <div className="h-full min-h-[200px] flex flex-col items-center justify-center text-center border border-dashed border-white/5 p-8">
                        <CalendarPlaceholder />
                        <p className="text-white/30 text-xs mt-3">Select a date to see available party time slots.</p>
                        {bookingsPaused && <p className="text-amber-500/60 text-[10px] mt-2">Bookings are currently paused — check back soon.</p>}
                      </div>
                    )}
                  </div>
                </div>

                {selectedDate && selectedSlot && !entireDateWithinLead && (
                  <div className="flex justify-end mt-6">
                    <button
                      type="button"
                      onClick={() => { setStep(2); setError(''); }}
                      className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-sm tracking-widest px-8 py-3.5 transition-colors"
                    >
                      CONTINUE <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </motion.div>
            )}

            {/* ── STEP 2: DETAILS & PAYMENT ── */}
            {step === 2 && (
              <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <form onSubmit={handleCardPay} className="space-y-5">
                  {/* Booking summary */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs border border-white/5 bg-[#111115] px-4 py-3">
                    <span className="text-white/30 font-bold tracking-widest text-[9px]">YOUR PARTY</span>
                    <span className="text-white/50">{format(selectedDate, 'dd MMM yyyy')}</span>
                    <span className="text-white/20">·</span>
                    <span className="text-white/50">{selectedSlot}</span>
                    <span className="text-white/20">·</span>
                    <span className="text-primary font-bold">{packageName}</span>
                  </div>

                  <DriverCountSelector value={driverCount} onChange={setDriverCount} min={4} max={8} />

                  {/* Estimated total + deposit summary */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="border border-white/5 bg-[#111115] px-4 py-3">
                      <p className="text-white/30 text-[9px] font-bold tracking-widest">DEPOSIT DUE NOW</p>
                      <p className="text-primary font-black text-xl mt-0.5">£{DEPOSIT_AMOUNT}</p>
                      <p className="text-white/30 text-[10px] mt-0.5">via Square (card / Apple Pay / Google Pay)</p>
                    </div>
                    <div className="border border-white/5 bg-[#111115] px-4 py-3">
                      <p className="text-white/30 text-[9px] font-bold tracking-widest">EST. TOTAL (BALANCE AT EVENT)</p>
                      <p className="text-white font-black text-xl mt-0.5">£{estimatedTotal}</p>
                      <p className="text-white/30 text-[10px] mt-0.5">{driverCount} drivers × £{packagePrice} — deposit deducted</p>
                    </div>
                  </div>

                  {/* Organizer details */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>FIRST NAME *</label>
                      <Input value={form.first_name} onChange={(e) => set('first_name', e.target.value)} placeholder="First name" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>LAST NAME *</label>
                      <Input value={form.last_name} onChange={(e) => set('last_name', e.target.value)} placeholder="Last name" className={inputClass} />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>EMAIL *</label>
                      <Input type="email" value={form.contact_email} onChange={(e) => set('contact_email', e.target.value)} placeholder="you@email.com" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>MOBILE PHONE *</label>
                      <Input type="tel" value={form.contact_phone} onChange={(e) => set('contact_phone', e.target.value)} placeholder="07xxx xxx xxx" className={inputClass} />
                    </div>
                  </div>

                  {/* Birthday child */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>BIRTHDAY CHILD'S NAME</label>
                      <Input value={form.birthday_child_name} onChange={(e) => set('birthday_child_name', e.target.value)} placeholder="Birthday child" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>BIRTHDAY CHILD'S AGE</label>
                      <select
                        value={form.birthday_child_age}
                        onChange={(e) => set('birthday_child_age', e.target.value)}
                        className="seo-input h-11 bg-[#111115] border border-white/10"
                      >
                        <option value="">Select age</option>
                        {Array.from({ length: 8 }, (_, i) => i + 8).map((age) => (
                          <option key={age} value={age}>{age}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>DIETARY REQUIREMENTS / SPECIAL REQUESTS</label>
                    <Input value={form.special_requests} onChange={(e) => set('special_requests', e.target.value)} placeholder="Dietary, accessibility, etc." className={inputClass} />
                  </div>

                  {/* Age confirmation checkbox */}
                  <label className="flex items-start gap-3 cursor-pointer border border-white/10 bg-[#111115] px-4 py-3">
                    <input
                      type="checkbox"
                      checked={form.age_confirmation}
                      onChange={(e) => set('age_confirmation', e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-[#dd1f26] shrink-0"
                    />
                    <span className="text-xs text-white/60 leading-relaxed">
                      I confirm that all drivers attending will be between the ages of <span className="text-white font-bold">8 and 15</span>.
                    </span>
                  </label>

                  {/* Party booking terms */}
                  <div className="border border-white/10 bg-[#111115] px-4 py-3.5">
                    <p className="text-primary text-[10px] font-bold tracking-widest mb-2.5">PARTY BOOKING TERMS</p>
                    <ul className="space-y-1.5 text-xs text-white/50 leading-relaxed">
                      <li className="flex gap-2"><span className="text-primary mt-0.5">⚬</span><span><span className="text-white/70 font-medium">Deposit:</span> A £50 deposit is required to secure your party date and time.</span></li>
                      <li className="flex gap-2"><span className="text-primary mt-0.5">⚬</span><span><span className="text-white/70 font-medium">Balance:</span> Remaining balance is calculated on final driver numbers (minimum 4, maximum 8 drivers) and is payable 48 hours before your event via a secure online payment link.</span></li>
                      <li className="flex gap-2"><span className="text-primary mt-0.5">⚬</span><span><span className="text-white/70 font-medium">Cancellations &amp; Rescheduling:</span> Deposits are non-refundable. You may reschedule your party date free of charge with at least 7 days' advance notice.</span></li>
                      <li className="flex gap-2"><span className="text-primary mt-0.5">⚬</span><span><span className="text-white/70 font-medium">Final Headcount &amp; Sickness Policy:</span> Final payment is locked in 48 hours prior to your event. We do not offer cash refunds for drivers who drop out or fall ill on the day. However, you are always welcome to substitute another child or adult into their simulator! Alternatively, speak to our staff on arrival, and we will happily issue a Motion Reality Gift Voucher for the missing driver to use on a future visit. <span className="text-primary/80 font-medium">(Please note: The minimum charge of 4 drivers always applies).</span></span></li>
                    </ul>
                  </div>

                  {/* Apple Pay / Google Pay */}
                  <div className="space-y-2">
                    <SquareApplePayButton
                      amount={DEPOSIT_AMOUNT}
                      label="Motion Reality Party Deposit"
                      onToken={handleWalletToken}
                      onError={setError}
                      validate={validateContact}
                      disabled={loading}
                      containerId="sq-deposit-applepay"
                    />
                    <SquareGooglePayButton
                      amount={DEPOSIT_AMOUNT}
                      label="Motion Reality Party Deposit"
                      onToken={handleWalletToken}
                      onError={setError}
                      validate={validateContact}
                      disabled={loading}
                      containerId="sq-deposit-googlepay"
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-white/5" />
                    <span className="text-white/20 text-[10px] font-bold tracking-widest">OR PAY BY CARD</span>
                    <div className="flex-1 h-px bg-white/5" />
                  </div>

                  <div id="sq-deposit-card" className="min-h-[44px]" />

                  {error && <p className="text-red-400 text-xs font-medium">{error}</p>}

                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => { setStep(1); setError(''); }}
                      className="inline-flex items-center gap-1.5 border border-white/10 hover:border-white/30 text-white/60 hover:text-white font-bold text-xs tracking-wider px-5 py-3.5 transition-colors"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" /> BACK
                    </button>
                    <button
                      type="submit"
                      disabled={loading || !card}
                      className="flex-1 inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-sm tracking-widest py-3.5 transition-colors disabled:opacity-50"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                      PAY £{DEPOSIT_AMOUNT} DEPOSIT
                    </button>
                  </div>

                  <div className="flex items-center justify-center gap-1.5 text-white/30 text-[10px]">
                    <ShieldCheck className="w-3 h-3" />
                    Secure payment via Square. We never store your card details.
                  </div>
                </form>
              </motion.div>
            )}

            {/* ── STEP 3: SUCCESS ── */}
            {step === 3 && (
              <motion.div key="step3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="text-white font-black text-xl uppercase mb-2">Deposit Secured</h3>
                <p className="text-white/50 text-sm mb-6 leading-relaxed max-w-md mx-auto">
                  Your £{DEPOSIT_AMOUNT} deposit has been received and all rig slots for{' '}
                  <span className="text-white font-bold">{format(selectedDate, 'dd MMM yyyy')} at {selectedSlot}</span> are now reserved.
                  You'll receive an email with a secure payment link to confirm your final headcount and pay the balance 48 hours before your event.
                </p>
                <div className="inline-flex flex-wrap justify-center gap-3 text-xs text-white/40 mb-6">
                  <span>Package: <span className="text-white/60">{packageName}</span></span>
                  <span>·</span>
                  <span>Drivers: <span className="text-white/60">{driverCount}</span></span>
                  <span>·</span>
                  <span>Est. total: <span className="text-white/60">£{estimatedTotal}</span></span>
                </div>
                <button onClick={onClose} className="bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-6 py-2.5 transition-colors">
                  DONE
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

function CalendarPlaceholder() {
  return (
    <svg className="w-10 h-10 text-white/10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="5" width="18" height="16" rx="1" />
      <path d="M3 9h18M8 3v4M16 3v4" />
    </svg>
  );
}